import { observer } from "mobx-react";
import { Checkbox, Alert, Form, InputNumber, Divider, Select, TimePicker } from "antd";
import dayjs from "dayjs";

interface DispatchViewConfigStepProps {
  data: {
    dispatchViewImpactAssessmentEnabled: boolean;
    allowAggregationOnRoutedNode: boolean;
    maxTripsAllowed: number;
    enableCustomerPromiseTimeWindow: boolean;
    calculateCustomerPromiseTimeWindow: {
      computationStage: string[];
      nonPremiumJobs: {
        retainTimeWindows: boolean;
        roundToTheNearest: number;
        minutesBeforeETA: number;
        minutesAfterETA: number;
        applyEarliestCutoff: boolean;
        earliestCutoff: string;
        minBufferBeforeETA: number;
      };
      premiumJobs: {
        retainTimeWindows: boolean;
        roundToTheNearest: number;
        minutesBeforeETA: number;
        minutesAfterETA: number;
        applyEarliestCutoff: boolean;
        earliestCutoff: string;
        minBufferBeforeETA: number;
      };
    };
    autoDispatchRoute: boolean;
    forceDispatchAllTrips: boolean;
    mandatoryTripViolationReason: boolean;
  };
  modules: {
    dispatch: boolean;
  };
  onChange: (data: any) => void;
}

const DispatchViewConfigStep = observer(({ data, modules, onChange }: DispatchViewConfigStepProps) => {
  if (!modules.dispatch) {
    return (
      <div className="p-4">
        <Alert message="Dispatch View Not Selected" description="Dispatch View configuration is only applicable when Dispatch View module is selected." type="info" showIcon />
      </div>
    );
  }

  const handleChange = (field: string, value: any) => {
    const updates: any = { [field]: value };

    // When enabling Customer Promise Time Window, ensure the nested object exists
    if (field === "enableCustomerPromiseTimeWindow") {
      if (value) {
        // Create the nested object with default values when enabling
        updates.calculateCustomerPromiseTimeWindow = {
          computationStage: ["routeStart"],
          nonPremiumJobs: {
            retainTimeWindows: false,
            roundToTheNearest: 5,
            minutesBeforeETA: 30,
            minutesAfterETA: 30,
            applyEarliestCutoff: false,
            earliestCutoff: "",
            minBufferBeforeETA: 15,
          },
          premiumJobs: {
            retainTimeWindows: false,
            roundToTheNearest: 5,
            minutesBeforeETA: 30,
            minutesAfterETA: 30,
            applyEarliestCutoff: false,
            earliestCutoff: "",
            minBufferBeforeETA: 15,
          },
        };
      } else {
        // Remove the nested object when disabling
        const newData = { ...data, ...updates };
        delete newData.calculateCustomerPromiseTimeWindow;
        onChange(newData);
        return;
      }
    }

    onChange({ ...data, ...updates });
  };

  const handleNestedChange = (path: string, value: any) => {
    const keys = path.split(".");
    const newData = { ...data };

    let current: any = newData;
    for (let i = 0; i < keys.length - 1; i++) {
      current[keys[i]] = { ...current[keys[i]] };
      current = current[keys[i]];
    }
    current[keys[keys.length - 1]] = value;

    onChange(newData);
  };

  const computationStageOptions = [
    { label: "Route Start", value: "routeStart" },
    { label: "Trip Start", value: "tripStart" },
    { label: "Route Commit", value: "routeCommit" },
  ];

  const roundingOptions = [
    { label: "1 minute", value: 1 },
    { label: "5 minutes", value: 5 },
    { label: "10 minutes", value: 10 },
    { label: "15 minutes", value: 15 },
    { label: "30 minutes", value: 30 },
  ];

  return (
    <div className="p-4">
      <Alert message="Dispatch View Configuration" description="Configure settings specific to the Dispatch View." type="info" showIcon className="mb-6" />

      <Form layout="vertical">
        {/* Main Dispatch Settings */}
        <Form.Item label={<div className="font-bold">Impact Assessment</div>} className="mb-4">
          <Checkbox checked={data.dispatchViewImpactAssessmentEnabled} onChange={(e) => handleChange("dispatchViewImpactAssessmentEnabled", e.target.checked)}>
            Enable Impact Assessment
          </Checkbox>
        </Form.Item>

        <Form.Item label={<div className="font-bold">Aggregation on Routed Nodes</div>} className="mb-4">
          <Checkbox checked={data.allowAggregationOnRoutedNode} onChange={(e) => handleChange("allowAggregationOnRoutedNode", e.target.checked)}>
            Allow Aggregation on Routed Nodes?
          </Checkbox>
        </Form.Item>

        <Form.Item label={<div className="font-bold">Maximum Trips Allowed</div>} required className="mb-4">
          <InputNumber min={1} max={9} value={data.maxTripsAllowed} onChange={(value) => handleChange("maxTripsAllowed", value || 1)} style={{ width: "150px" }} />
          <div className="text-sm text-gray-500 mt-1">Range: 1-9 trips</div>
        </Form.Item>

        {/* Customer Promise Time Window Computation */}
        <Divider orientation="left">Customer Promise Time Window Computation</Divider>
        <Form.Item className="mb-4">
          <Checkbox checked={data.enableCustomerPromiseTimeWindow} onChange={(e) => handleChange("enableCustomerPromiseTimeWindow", e.target.checked)}>
            Apply Customer Promise Time Window
          </Checkbox>
        </Form.Item>

        {data.enableCustomerPromiseTimeWindow && data.calculateCustomerPromiseTimeWindow && (
          <>
            <Form.Item label="Computation Stage" className="mb-4">
              <Select
                mode="multiple"
                style={{ width: "100%" }}
                placeholder="Select computation stages"
                value={data.calculateCustomerPromiseTimeWindow.computationStage}
                onChange={(value) => handleNestedChange("calculateCustomerPromiseTimeWindow.computationStage", value)}
                options={computationStageOptions}
              />
            </Form.Item>

            {/* Non-Premium Jobs */}
            <Divider orientation="left">Non-Premium Jobs</Divider>
            <Form.Item className="mb-4">
              <Checkbox
                checked={data.calculateCustomerPromiseTimeWindow.nonPremiumJobs.retainTimeWindows}
                onChange={(e) => handleNestedChange("calculateCustomerPromiseTimeWindow.nonPremiumJobs.retainTimeWindows", e.target.checked)}
              >
                Retain Job Time Window?
              </Checkbox>
            </Form.Item>

            {!data.calculateCustomerPromiseTimeWindow.nonPremiumJobs.retainTimeWindows && (
              <>
                <Form.Item label="Rounding to the nearest minutes" className="mb-4">
                  <Select
                    style={{ width: "200px" }}
                    placeholder="Select rounding"
                    value={data.calculateCustomerPromiseTimeWindow.nonPremiumJobs.roundToTheNearest}
                    onChange={(value) => handleNestedChange("calculateCustomerPromiseTimeWindow.nonPremiumJobs.roundToTheNearest", value)}
                    options={roundingOptions}
                  />
                </Form.Item>

                <Form.Item label="Minutes before ETA" className="mb-4">
                  <InputNumber
                    min={5}
                    max={240}
                    value={data.calculateCustomerPromiseTimeWindow.nonPremiumJobs.minutesBeforeETA}
                    onChange={(value) => handleNestedChange("calculateCustomerPromiseTimeWindow.nonPremiumJobs.minutesBeforeETA", value || 5)}
                    style={{ width: "150px" }}
                  />
                  <div className="text-sm text-gray-500 mt-1">Range: 5-240 minutes</div>
                </Form.Item>

                <Form.Item label="Minutes after ETA" className="mb-4">
                  <InputNumber
                    min={5}
                    max={240}
                    value={data.calculateCustomerPromiseTimeWindow.nonPremiumJobs.minutesAfterETA}
                    onChange={(value) => handleNestedChange("calculateCustomerPromiseTimeWindow.nonPremiumJobs.minutesAfterETA", value || 5)}
                    style={{ width: "150px" }}
                  />
                  <div className="text-sm text-gray-500 mt-1">Range: 5-240 minutes</div>
                </Form.Item>

                <Form.Item className="mb-4">
                  <Checkbox
                    checked={data.calculateCustomerPromiseTimeWindow.nonPremiumJobs.applyEarliestCutoff}
                    onChange={(e) => handleNestedChange("calculateCustomerPromiseTimeWindow.nonPremiumJobs.applyEarliestCutoff", e.target.checked)}
                  >
                    Apply Earliest Cut Off
                  </Checkbox>
                </Form.Item>

                {data.calculateCustomerPromiseTimeWindow.nonPremiumJobs.applyEarliestCutoff && (
                  <>
                    <Form.Item label="Earliest Cut Off Time" className="ml-6 mb-4">
                      <TimePicker
                        format="HH:mm:ss"
                        value={data.calculateCustomerPromiseTimeWindow.nonPremiumJobs.earliestCutoff ? dayjs(data.calculateCustomerPromiseTimeWindow.nonPremiumJobs.earliestCutoff, "HH:mm:ss") : null}
                        onChange={(time) => handleNestedChange("calculateCustomerPromiseTimeWindow.nonPremiumJobs.earliestCutoff", time ? time.format("HH:mm:ss") : "")}
                        style={{ width: "150px" }}
                      />
                    </Form.Item>

                    <Form.Item label="Minimum buffer before ETA (minutes)" className="ml-6 mb-4">
                      <InputNumber
                        min={5}
                        max={60}
                        value={data.calculateCustomerPromiseTimeWindow.nonPremiumJobs.minBufferBeforeETA}
                        onChange={(value) => handleNestedChange("calculateCustomerPromiseTimeWindow.nonPremiumJobs.minBufferBeforeETA", value || 5)}
                        style={{ width: "150px" }}
                      />
                      <div className="text-sm text-gray-500 mt-1">Range: 5-60 minutes</div>
                    </Form.Item>
                  </>
                )}
              </>
            )}

            {/* Premium Jobs */}
            <Divider orientation="left">Premium Jobs</Divider>
            <Form.Item className="mb-4">
              <Checkbox
                checked={data.calculateCustomerPromiseTimeWindow.premiumJobs.retainTimeWindows}
                onChange={(e) => handleNestedChange("calculateCustomerPromiseTimeWindow.premiumJobs.retainTimeWindows", e.target.checked)}
              >
                Retain Job Time Window?
              </Checkbox>
            </Form.Item>

            {!data.calculateCustomerPromiseTimeWindow.premiumJobs.retainTimeWindows && (
              <>
                <Form.Item label="Rounding to the nearest minutes" className="mb-4">
                  <Select
                    style={{ width: "200px" }}
                    placeholder="Select rounding"
                    value={data.calculateCustomerPromiseTimeWindow.premiumJobs.roundToTheNearest}
                    onChange={(value) => handleNestedChange("calculateCustomerPromiseTimeWindow.premiumJobs.roundToTheNearest", value)}
                    options={roundingOptions}
                  />
                </Form.Item>

                <Form.Item label="Minutes before ETA" className="mb-4">
                  <InputNumber
                    min={5}
                    max={240}
                    value={data.calculateCustomerPromiseTimeWindow.premiumJobs.minutesBeforeETA}
                    onChange={(value) => handleNestedChange("calculateCustomerPromiseTimeWindow.premiumJobs.minutesBeforeETA", value || 5)}
                    style={{ width: "150px" }}
                  />
                  <div className="text-sm text-gray-500 mt-1">Range: 5-240 minutes</div>
                </Form.Item>

                <Form.Item label="Minutes after ETA" className="mb-4">
                  <InputNumber
                    min={5}
                    max={240}
                    value={data.calculateCustomerPromiseTimeWindow.premiumJobs.minutesAfterETA}
                    onChange={(value) => handleNestedChange("calculateCustomerPromiseTimeWindow.premiumJobs.minutesAfterETA", value || 5)}
                    style={{ width: "150px" }}
                  />
                  <div className="text-sm text-gray-500 mt-1">Range: 5-240 minutes</div>
                </Form.Item>

                <Form.Item className="mb-4">
                  <Checkbox
                    checked={data.calculateCustomerPromiseTimeWindow.premiumJobs.applyEarliestCutoff}
                    onChange={(e) => handleNestedChange("calculateCustomerPromiseTimeWindow.premiumJobs.applyEarliestCutoff", e.target.checked)}
                  >
                    Apply Earliest Cut Off
                  </Checkbox>
                </Form.Item>

                {data.calculateCustomerPromiseTimeWindow.premiumJobs.applyEarliestCutoff && (
                  <>
                    <Form.Item label="Earliest Cut Off Time" className="ml-6 mb-4">
                      <TimePicker
                        format="HH:mm:ss"
                        value={data.calculateCustomerPromiseTimeWindow.premiumJobs.earliestCutoff ? dayjs(data.calculateCustomerPromiseTimeWindow.premiumJobs.earliestCutoff, "HH:mm:ss") : null}
                        onChange={(time) => handleNestedChange("calculateCustomerPromiseTimeWindow.premiumJobs.earliestCutoff", time ? time.format("HH:mm:ss") : "")}
                        style={{ width: "150px" }}
                      />
                    </Form.Item>

                    <Form.Item label="Minimum buffer before ETA (minutes)" className="ml-6 mb-4">
                      <InputNumber
                        min={5}
                        max={60}
                        value={data.calculateCustomerPromiseTimeWindow.premiumJobs.minBufferBeforeETA}
                        onChange={(value) => handleNestedChange("calculateCustomerPromiseTimeWindow.premiumJobs.minBufferBeforeETA", value || 5)}
                        style={{ width: "150px" }}
                      />
                      <div className="text-sm text-gray-500 mt-1">Range: 5-60 minutes</div>
                    </Form.Item>
                  </>
                )}
              </>
            )}
          </>
        )}

        {/* Additional Dispatch Settings */}
        <Divider orientation="left">Additional Settings</Divider>
        <Form.Item className="mb-4">
          <Checkbox checked={data.autoDispatchRoute} onChange={(e) => handleChange("autoDispatchRoute", e.target.checked)}>
            Automatically Dispatch Route?
          </Checkbox>
        </Form.Item>

        <Form.Item className="mb-4">
          <Checkbox checked={data.forceDispatchAllTrips} onChange={(e) => handleChange("forceDispatchAllTrips", e.target.checked)}>
            Force user to dispatch all trips at once?
          </Checkbox>
        </Form.Item>

        <Form.Item className="mb-4">
          <Checkbox checked={data.mandatoryTripViolationReason} onChange={(e) => handleChange("mandatoryTripViolationReason", e.target.checked)}>
            Mandatory Trip Violation Reason
          </Checkbox>
        </Form.Item>
      </Form>
    </div>
  );
});

export default DispatchViewConfigStep;
